import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { X } from "react-bootstrap-icons";
import { FaUser } from "react-icons/fa";
import clsx from "clsx";
import ResponsiveWrapper from "@/components/common/ResponsiveWrapper";
import useMixpanelEvent from "@/hooks/useMixpanelEvent";
import { useUser } from "@/context/UserContext";
import {
    COMMENT_LIKE_TARGET_TYPE,
    PLAYABLE_PROPERTY_LIKE_TARGET_TYPE,
} from "@/constants/like.constants";
import { getLikers, type LikerUser } from "@/services/LikeService";
import type { LikesDrawerState } from "@/components/common/likes-drawer/likes-drawer.types";

interface LikesDrawerProps {
    state: LikesDrawerState | null;
    onClose: () => void;
}

function mapContentTypeToTargetType(contentType: LikesDrawerState["contentType"]): typeof PLAYABLE_PROPERTY_LIKE_TARGET_TYPE | typeof COMMENT_LIKE_TARGET_TYPE {
    return contentType === "puzzle" ? PLAYABLE_PROPERTY_LIKE_TARGET_TYPE : COMMENT_LIKE_TARGET_TYPE;
}

function LikerRow({
    liker,
    onSelect,
}: {
    liker: LikerUser;
    onSelect: (nickname: string) => void;
}): React.ReactElement {
    const [hasImageError, setHasImageError] = React.useState(false);
    const nickname = liker.nickname?.trim() || "User";
    const showPhoto = Boolean(liker.profilePhotoUrl?.trim()) && !hasImageError;
    return (
        <button
            type="button"
            onClick={() => onSelect(nickname)}
            className="tw-flex tw-w-full tw-items-center tw-gap-3 tw-rounded-lg tw-border-0 tw-bg-transparent tw-px-3 tw-py-2.5 tw-text-left hover:tw-bg-gray-100 active:tw-bg-gray-200"
        >
            <div className="tw-relative tw-h-10 tw-w-10 tw-flex-shrink-0 tw-overflow-hidden tw-rounded-full tw-bg-gray-100">
                {showPhoto ? (
                    <img
                        src={liker.profilePhotoUrl!.trim()}
                        alt=""
                        className="tw-h-full tw-w-full tw-object-cover"
                        onError={() => setHasImageError(true)}
                    />
                ) : (
                    <div className="tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center tw-text-gray-400">
                        <FaUser className="tw-text-base" aria-hidden="true" />
                    </div>
                )}
            </div>
            <span className="tw-text-sm tw-font-semibold tw-text-gray-900">{nickname}</span>
        </button>
    );
}

/**
 * Drawer / modal listing users who liked a puzzle or comment.
 */
const LikesDrawer: React.FC<LikesDrawerProps> = ({ state, onClose }) => {
    const navigate = useNavigate();
    const { userState } = useUser();
    const { sendEvent: sendMixpanelEvent } = useMixpanelEvent();
    const scrollRef = useRef<HTMLDivElement | null>(null);
    const isOpen = Boolean(state?.isOpen);
    const contentType = state?.contentType ?? "puzzle";
    const contentId = state?.contentId ?? "";
    const likeCount = state?.likeCount ?? 0;
    const location = state?.location ?? "Feed";
    const targetType = mapContentTypeToTargetType(contentType);
    const {
        data,
        isLoading,
        isError,
        isFetchingNextPage,
        hasNextPage,
        fetchNextPage,
    } = useInfiniteQuery({
        queryKey: ["likers", targetType, contentId],
        queryFn: ({ pageParam }) =>
            getLikers({
                targetType,
                targetId: contentId,
                page: pageParam,
            }),
        initialPageParam: 1,
        getNextPageParam: (lastPage) => (lastPage.hasNextPage ? lastPage.page + 1 : undefined),
        enabled: isOpen && Boolean(contentId),
    });
    const likers = useMemo(() => {
        const rows = data?.pages.flatMap((page) => (Array.isArray(page?.data) ? page.data : [])) ?? [];
        return rows.filter((liker): liker is LikerUser => Boolean(liker?.id && liker?.nickname?.trim()));
    }, [data?.pages]);
    const trackedContentKeyRef = useRef<string | null>(null);
    useEffect(() => {
        if (!isOpen) {
            trackedContentKeyRef.current = null;
            return;
        }
        if (!state) {
            return;
        }
        const contentKey = `${state.contentType}:${state.contentId}`;
        if (trackedContentKeyRef.current === contentKey) {
            return;
        }
        trackedContentKeyRef.current = contentKey;
        if (isOpen) {
            sendMixpanelEvent("Likes Drawer Opened", {
                content_type: state.contentType,
                content_id: state.contentId,
                like_count: state.likeCount,
                location: state.location,
                pageUrl: window.location.href,
                email: userState?.emailAddress ?? null,
                nickname: userState?.nickname ?? null,
            });
        }
    }, [isOpen, state, sendMixpanelEvent, userState?.emailAddress, userState?.nickname]);
    const handleLikerSelect = useCallback(
        (nickname: string): void => {
            sendMixpanelEvent("Liker Profile Tapped", {
                content_type: contentType,
                content_id: contentId,
                nickname_tapped: nickname,
                location,
                pageUrl: window.location.href,
                email: userState?.emailAddress ?? null,
                nickname: userState?.nickname ?? null,
            });
            onClose();
            navigate(`/profile/${encodeURIComponent(nickname.trim())}`);
        },
        [
            contentId,
            contentType,
            location,
            navigate,
            onClose,
            sendMixpanelEvent,
            userState?.emailAddress,
            userState?.nickname,
        ],
    );
    const handleScroll = useCallback((): void => {
        const container = scrollRef.current;
        if (!container || isFetchingNextPage || !hasNextPage) {
            return;
        }
        const { scrollTop, scrollHeight, clientHeight } = container;
        if (scrollTop + clientHeight >= scrollHeight - 48) {
            void fetchNextPage();
        }
    }, [fetchNextPage, hasNextPage, isFetchingNextPage]);
    useEffect(() => {
        const container = scrollRef.current;
        if (!container || !isOpen) {
            return;
        }
        container.addEventListener("scroll", handleScroll);
        return () => container.removeEventListener("scroll", handleScroll);
    }, [handleScroll, isOpen, likers.length]);
    return (
        <ResponsiveWrapper
            show={isOpen}
            onHide={onClose}
            centered
            drawerHeight="65vh"
            dialogClassName="!tw-max-w-[420px] !tw-mx-auto"
            contentClassName="!tw-border-0 !tw-rounded-2xl !tw-overflow-hidden !tw-min-h-[320px] !tw-max-h-[min(520px,80vh)]"
        >
            <div className="tw-relative tw-flex tw-h-full tw-flex-col tw-overflow-hidden tw-bg-white tw-rounded-t-2xl sm:tw-rounded-2xl sm:tw-h-[520px]">
                <div className="tw-flex tw-flex-shrink-0 tw-items-center tw-justify-between tw-border-b tw-border-gray-100 tw-px-4 tw-py-3">
                    <div className="tw-flex tw-items-baseline tw-gap-2">
                        <h2 className="tw-m-0 tw-text-base tw-font-bold tw-text-gray-900">Likes</h2>
                        <span className="tw-text-sm tw-font-normal tw-text-gray-400">{likeCount}</span>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close likes drawer"
                        className="tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-transparent tw-text-gray-500 hover:tw-bg-gray-100"
                    >
                        <X className="tw-h-5 tw-w-5" aria-hidden="true" />
                    </button>
                </div>
                <div
                    ref={scrollRef}
                    className={clsx(
                        "tw-flex-1 tw-overflow-y-auto tw-px-1 tw-py-1",
                        (isLoading || isError) && likers.length === 0 && "tw-flex tw-items-center tw-justify-center",
                    )}
                >
                    {isLoading && likers.length === 0 ? (
                        <p className="tw-m-0 tw-text-sm tw-text-gray-400">Loading...</p>
                    ) : isError && likers.length === 0 ? (
                        <p className="tw-m-0 tw-text-sm tw-text-gray-400">Unable to load likes</p>
                    ) : likers.length === 0 ? (
                        <p className="tw-m-0 tw-text-sm tw-text-gray-400">No likes yet</p>
                    ) : (
                        <>
                            {likers.map((liker) => (
                                <LikerRow key={liker.id} liker={liker} onSelect={handleLikerSelect} />
                            ))}
                            {isFetchingNextPage && (
                                <p className="tw-py-3 tw-text-center tw-text-sm tw-text-gray-400">Loading more...</p>
                            )}
                        </>
                    )}
                </div>
            </div>
        </ResponsiveWrapper>
    );
};

export default LikesDrawer;
